import { AccessLevel } from '@/__generated__/graphql';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { usePublicModels } from '@/hooks/discovery/publicModels';
import { useResourceByEnvironment } from '@/hooks/discovery/resource';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import { getAccess, getTrustSignals } from '@/util/dbt/resourceAppliedNode';
import { ResourcePanelDetails } from './ResourcePanelDetails';
import { ResourcePanelHeader } from './ResourcePanelHeader';

export type ResourcePanelViewProps = {
  uniqueId: ParsedUniqueId | null;
  nodeEnvironmentId?: number;
};

export const ResourcePanelContents: React.FC<ResourcePanelViewProps> = ({
  uniqueId,
  nodeEnvironmentId,
}) => {
  const { accountId, environmentId } = useDbtCloudProject();
  const { userId } = useEnvironmentScope();
  const [resourceQuery] = useResourceByEnvironment({
    environmentId,
    userId,
    uniqueId: uniqueId?.raw,
  });
  const currentNode =
    resourceQuery.data?.environment?.applied?.resources?.edges?.[0]?.node;
  const isExternalModel = !currentNode;

  const publicModelsQuery = usePublicModels({
    first: 1,
    accountId,
    environmentIds: [nodeEnvironmentId!],
    uniqueIds: [uniqueId?.raw!],
    pause: !isExternalModel || !uniqueId?.raw,
  });
  const publicModel = publicModelsQuery.data?.find(
    (node) => node.uniqueId === uniqueId?.raw,
  );

  const [{ data: fullPublicModelData }] = useResourceByEnvironment({
    environmentId: publicModel?.environmentId!,
    userId,
    uniqueId: uniqueId?.raw!,
  });

  const fullPublicModel =
    fullPublicModelData?.environment.applied?.resources?.edges?.[0]?.node;

  const currentResource = isExternalModel ? fullPublicModel : currentNode;

  if (!uniqueId) return null;

  const files: string[] = [
    ...(currentNode && 'patchPath' in currentNode && currentNode.patchPath
      ? [currentNode.patchPath]
      : []),
    ...(currentNode?.filePath ? [currentNode.filePath] : []),
  ];

  return (
    <div
      className="h-full overflow-y-auto overflow-x-hidden"
      data-testid="resource-panel"
    >
      <ResourcePanelHeader
        resourceType={uniqueId.resourceType}
        uniqueId={uniqueId.raw}
        environmentId={nodeEnvironmentId!}
        files={files}
        // TODO: add isExternalModel prop to ResourcePanelHeader to prevent unnecessary api calls for external models
      />
      <ResourcePanelDetails
        uniqueId={uniqueId}
        name={currentResource?.name || undefined}
        projectId={currentResource?.projectId || undefined}
        environmentId={isExternalModel ? publicModel?.environmentId! : 0}
        access={
          (isExternalModel ? AccessLevel.Public : getAccess(currentNode)) || undefined
        }
        isExternalModel={isExternalModel}
        trustSignals={getTrustSignals(currentNode)}
        resource={currentResource}
      />
    </div>
  );
};
